<script>
  import { createEventDispatcher } from 'svelte';

  export let value = '';
  export let label = 'Timezone';
  export let required = false;

  const dispatch = createEventDispatcher();

  // All IANA timezones, grouped by region, with a short offset label
  // Generated from Intl.supportedValuesOf('timeZone') at build-time equivalent
  const TIMEZONES = (() => {
    const raw = Intl.supportedValuesOf('timeZone');
    const now = Date.now();
    return raw.map(tz => {
      const offset = new Intl.DateTimeFormat('en', {
        timeZone: tz,
        timeZoneName: 'shortOffset',
      })
        .formatToParts(now)
        .find(p => p.type === 'timeZoneName')?.value ?? '';

      // Derive a short region prefix from the first segment
      const region = tz.split('/')[0];
      const city   = tz.split('/').slice(1).join('/').replace(/_/g, ' ') || tz;

      return { tz, region, city, offset };
    });
  })();

  let query = value
    ? (TIMEZONES.find(t => t.tz === value)?.tz ?? value)
    : '';
  let showDropdown = false;

  $: filtered = query.length === 0
    ? TIMEZONES
    : TIMEZONES.filter(t =>
        t.tz.toLowerCase().includes(query.toLowerCase()) ||
        t.city.toLowerCase().includes(query.toLowerCase()) ||
        t.offset.toLowerCase().includes(query.toLowerCase())
      );

  $: selectedTz = TIMEZONES.find(t => t.tz === value) ?? null;

  function select(item) {
    value = item.tz;
    query = item.tz;
    showDropdown = false;
    dispatch('change', item.tz);
  }

  function handleInput() {
    value = '';
    dispatch('change', '');
    showDropdown = true;
  }

  function handleFocus() {
    showDropdown = true;
  }

  function handleBlur() {
    setTimeout(() => {
      showDropdown = false;
      if (!selectedTz) {
        query = '';
        value = '';
        dispatch('change', '');
      }
    }, 160);
  }
</script>

<div class="search-field">
  <label>
    {label}
    {#if required}<span class="req">*</span>{/if}
  </label>
  <div class="input-wrap">
    <input
      type="text"
      bind:value={query}
      on:input={handleInput}
      on:focus={handleFocus}
      on:blur={handleBlur}
      placeholder="Search timezone…"
      autocomplete="off"
    />
    {#if selectedTz}
      <span class="badge check">✓</span>
    {/if}
  </div>

  {#if showDropdown && filtered.length > 0}
    <div class="dropdown">
      {#each filtered.slice(0, 120) as item (item.tz)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="dropdown-item" on:click={() => select(item)}>
          <span class="item-offset">{item.offset}</span>
          <span class="item-tz">{item.tz}</span>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  .search-field { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.85rem; position: relative; }

  label {
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.55rem;
    letter-spacing: 0.25em; text-transform: uppercase; color: #3a6b00;
  }

  .req { color: #cc4400; margin-left: 2px; }

  .input-wrap { position: relative; }

  input {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 1rem; font-weight: 600;
    padding: 0.6rem 2rem 0.6rem 0.75rem;
    width: 100%; outline: none;
    transition: border-color 0.2s, box-shadow 0.2s; box-sizing: border-box;
  }
  input:focus { border-color: #3a6b00; box-shadow: 0 0 0 2px rgba(74,124,0,0.15); }

  .badge {
    position: absolute; right: 0.6rem; top: 50%; transform: translateY(-50%);
  }
  .check { color: #6db800; font-size: 0.85rem; font-weight: bold; }

  .dropdown {
    position: absolute; top: 100%; left: 0; right: 0;
    background: #0f0f0f; border: 1px solid #2a5200;
    border-top: none; border-radius: 0 0 3px 3px;
    z-index: 300; box-shadow: 0 8px 24px rgba(0,0,0,0.8);
    max-height: 220px; overflow-y: auto;
  }

  .dropdown-item {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 0.75rem; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 600;
    color: #c8c8c8; border-bottom: 1px solid #0f1a00; transition: background 0.15s;
  }
  .dropdown-item:hover { background: #0d1a00; }
  .dropdown-item:last-child { border-bottom: none; }

  .item-offset {
    font-size: 0.65rem; font-weight: 700; letter-spacing: 0.1em;
    color: #3a6b00; min-width: 3.8rem; flex-shrink: 0;
  }
  .item-tz { flex: 1; }
</style>
